/** Kept for older call sites; the candidate layer no longer exists. */
export type Layer = 'terms' | 'raw'

/** A term base: its own documents, terms and concepts. */
export interface Collection {
  id: number
  name: string
  slug: string
  description: string
  document_count: number
  term_count: number
  concept_count: number
  /** Terms not in any concept. Never reaches zero: a term with no partner
   *  anywhere stays ungrouped by design. */
  ungrouped_count?: number
  /** Terms a build has never judged — the number that actually means "work
   *  left to do". */
  unchecked_count?: number
  /** This user's role in the collection. */
  my_role?: 'owner' | 'editor' | 'viewer'
  owner_id?: number | null
}

/**
 * Replaces the old candidate/curated split. 'new' is untouched machine output
 * and is the only status re-extraction is allowed to replace.
 */
export type ReviewStatus = 'new' | 'promoted' | 'reviewed' | 'approved' | 'rejected'

export type TermType = 'term' | 'abbreviation' | string

export interface Term {
  term_id: number
  value: string
  lang: string
  domain: string
  /** review_status from the API. */
  status: string
  term_type: TermType
  freq: number
  doc_count: number
  concept_id?: number | null
  collection_id?: number | null
}

export interface TermListResponse {
  /** NB: the backend returns the length of `terms`, not the unfiltered total. */
  total: number
  terms: Term[]
}

export interface Mention {
  surface: string
  sentence: string
  page: number
  document: string
  /** Opens the document's text view; absent for mentions of a deleted document. */
  document_id?: number | null
  /** The original PDF is stored and can be opened at `page`. */
  has_file?: boolean
  url: string | null
}

export interface MentionsResponse {
  mentions: Mention[]
}

export interface ConceptTerm {
  term_id?: number
  value: string
  lang: string
  term_type: TermType
  is_preferred: boolean
  freq?: number
  docs?: number
  context?: string | null
  context_source?: string | null
  /** The citation's surface form, page and document — same shape a Mention has. */
  context_surface?: string | null
  context_page?: number
  context_document_id?: number | null
  context_url?: string | null
  context_has_file?: boolean
}

export interface ConceptDefinition {
  /** 'seletus (tsitaat)' = verbatim quote from a document; 'autogen' = machine draft. */
  type: string
  lang: string
  definition: string
  source: string
  /** Quotes: where the sentence was found. */
  document_id?: number | null
  page?: number
  url?: string | null
  has_file?: boolean
  /** Drafts: which model wrote it and from what ('quotes' | 'contexts'). */
  model?: string | null
  based_on?: string | null
  date_created?: string | null
}

export interface Concept {
  concept_id: number
  status: string
  domains: string
  terms: ConceptTerm[]
  definitions?: ConceptDefinition[]
}

export interface ConceptListResponse {
  total: number
  concepts: Concept[]
}

/** Languages the term base actually uses, in display order. */
export const LANGUAGES = ['et', 'en', 'fi'] as const
